import React, { useEffect, useState } from "react";
import colors from "../../theme";
import { fetchDevices, fetchChargingSessions } from "../../services/api";
import SnackbarComp from "../../components/Snackbar/SnackbarComp";
import { useSelector } from "react-redux";

import {
  Box,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  useMediaQuery,
  styled,
  CircularProgress,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import updateLocale from "dayjs/plugin/updateLocale";
import dayjs from "dayjs";
import TableDatagrid from "../../components/TableDatagrid/TableDatagrid";

import {
  datePickerSlotProps,
  datePickerSlots,
  datePickerSx,
} from "../../styles/datePickerStyles";

// date format
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});

const SmallHeader = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.6rem",
  },
}));

const formatPlatformFeeDisplay = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "-";
};

const formatSessionTimestamp = (value) => {
  if (!value) {
    return null;
  }
  const date = dayjs(value);
  if (!date.isValid()) {
    return null;
  }
  if (date.year() === dayjs().year()) {
    return date.format("MMM D HH:mm");
  }
  return date.format("DD/MM/YYYY HH:mm");
};

const ChargingSessions = () => {
  const currency = useSelector((state) => state.currency.currency);

  const [chargingPoints, setChargingPoints] = useState([]);
  const [selectedChargingPoint, setSelectedChargingPoint] = useState("");
  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const mobilecolumns = [
    {
      field: "fromTo",
      headerName: "From / To",
      flex: 1.9,
      headerClassName: "data-grid-header",
      renderCell: (params) => {
        return (
          <Box>
            {params.row.fromTo.start} <br />
            {params.row.fromTo.end}
          </Box>
        );
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 1.2,
      headerClassName: "data-grid-header",
    },
    {
      field: "energyConsumed",
      headerName: "Energy Consumed (kWh)",
      flex: 1,
      headerClassName: "data-grid-header",
      renderHeader: () => (
        <SmallHeader>
          <Box>
            Energy
            <br />
            (kWh)
          </Box>
        </SmallHeader>
      ),
    },
    {
      field: "energyRate",
      headerName: `Tariff (${currency}/kWh)`,
      flex: 1.1,
      headerClassName: "data-grid-header",
      renderHeader: () => (
        <SmallHeader>
          <Box>
            Tariff
            <br />({currency}/kWh)
          </Box>
        </SmallHeader>
      ),
    },
    {
      field: "totalEnergyCost",
      headerName: `Total Cost (${currency})`,
      flex: 1,
      headerClassName: "data-grid-header",
      renderHeader: () => (
        <SmallHeader>
          <Box>
            Cost
            <br />({currency})
          </Box>
        </SmallHeader>
      ),
    },
    {
      field: "platformFee",
      headerName: `Platform Fees (${currency})`,
      flex: 1,
      headerClassName: "data-grid-header",
      renderHeader: () => (
        <SmallHeader>
          <Box>
            Platform
            <br />({currency})
          </Box>
        </SmallHeader>
      ),
    },
    {
      field: "totalTariff",
      headerName: `Total Tariff (${currency})`,
      flex: 1,
      headerClassName: "data-grid-header",
      renderHeader: () => (
        <SmallHeader>
          <Box>
            Total Tariff
            <br />({currency})
          </Box>
        </SmallHeader>
      ),
    },
  ];

  const desktopColumns = [
    {
      field: "fromTo",
      headerName: "From / To",
      flex: 1.2,
      headerClassName: "data-grid-header",
      renderCell: (params) => {
        return (
          <div>
            {params.row.fromTo.start} - {params.row.fromTo.end}
          </div>
        );
      },
    },
    {
      field: "duration",
      headerName: "Duration",
      flex: 0.7,
      headerClassName: "data-grid-header",
    },
    {
      field: "energyConsumed",
      headerName: "Energy Consumed (kWh)",
      flex: 1,
      headerClassName: "data-grid-header",
    },
    {
      field: "energyRate",
      headerName: `Tariff (${currency}/kWh)`,
      flex: 0.7,
      headerClassName: "data-grid-header",
    },
    {
      field: "totalEnergyCost",
      headerName: `Total Cost (${currency})`,
      flex: 0.6,
      headerClassName: "data-grid-header",
    },
    {
      field: "platformFee",
      headerName: `Platform Fees (${currency})`,
      flex: 0.7,
      headerClassName: "data-grid-header",
    },
    {
      field: "totalTariff",
      headerName: `Total Tariff (${currency})`,
      flex: 0.7,
      headerClassName: "data-grid-header",
    },
  ];

  useEffect(() => {
    const fetchDevicesData = async () => {
      setIsPageLoading(true);
      try {
        const devices = await fetchDevices();
        setChargingPoints(devices);
        if (devices.length > 0) {
          setSelectedChargingPoint(devices[0].chargePointId);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsPageLoading(false);
      }
    };
    fetchDevicesData();
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setIsLoading(true);
        const sessionsResponse = await fetchChargingSessions(
          selectedChargingPoint,
          fromDate,
          toDate
        );
        const sessions = sessionsResponse.map((session, index) => {
          const start = dayjs(session.startedAt);
          const end = session.endedAt ? dayjs(session.endedAt) : null;
          const diffInMinutes = end ? end.diff(start, "minute") : 0;
          const hours = Math.floor(diffInMinutes / 60);
          const minutes = diffInMinutes % 60;
          const duration = `${hours}h ${minutes}m`;
          return {
            id: session.id ?? index,
            sessionId: session.sessionId ?? session.id ?? index,
            segmentIndex: Number(String(session.id).split("-")[1]) || 0,
            fromTo: {
              start: formatSessionTimestamp(start),
              end: end ? formatSessionTimestamp(end) : null,
            },
            duration: duration,
            energyConsumed: (session.energyConsumed / 1000).toFixed(2),
            energyRate: session.energyRate,
            totalEnergyCost: session.totalEnergyCost,
            platformFee: formatPlatformFeeDisplay(session.platformFee),
            totalTariff: session.totalTariff,
          };
        });
        setData(sessions);
      } catch (error) {
        console.error("Error fetching charging sessions:", error);
        setError(error.message);
        setStatusCode(error.response ? error.response.status : null);
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    };
    if (selectedChargingPoint) {
      fetchSessions();
    }
  }, [selectedChargingPoint, fromDate, toDate]);

  const handleChargingPointChange = (event) => {
    setSelectedChargingPoint(event.target.value);
  };

  const handleFromDateChange = (date) => {
    if (date.isAfter(toDate)) {
      setToDate(date);
    }
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    if (date.isBefore(fromDate)) {
      setFromDate(date);
    }
    setToDate(date);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  if (isPageLoading) {
    return (
      <Box
        component="main"
        sx={{ flex: 1, py: 6, px: 4, bgcolor: colors.mainContentBgColor }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        component="main"
        sx={{ flex: 1, px: 0, bgcolor: colors.mainContentBgColor }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            mx: { xs: "1rem", md: "2.5rem" },
            my: 1.5,
          }}
        >
          <Box
            sx={{
              flex: { xs: "none", md: "1" },
              minWidth: { xs: "100%", md: "auto" },
              mr: { xs: 0, md: "1rem" },
            }}
          >
            <FormControl
              fullWidth
              variant="standard"
              sx={{
                width: { sm: "FullWidth", md: "12rem" },
                "& .MuiInput-underline:before": {
                  borderBottomColor: colors.secondary,
                },
                "& .MuiInput-underline:after": {
                  borderBottomColor: colors.secondary,
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: colors.secondary,
                },
              }}
            >
              <InputLabel id="charging-point-select-label">
                Device
              </InputLabel>
              <Select
                sx={{
                  mb: { xs: "1rem", md: 0 },
                }}
                labelId="charging-point-select-label"
                value={selectedChargingPoint}
                onChange={handleChargingPointChange}
                label="Select Device"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: "#eaeff1",
                    },
                  },
                }}
              >
                {chargingPoints.map((point) => (
                  <MenuItem
                    key={point.chargePointId}
                    value={point.chargePointId}
                  >
                    {point.chargePointId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" alignItems="center" gap="1rem">
              <DatePicker
                label="From"
                value={fromDate}
                onChange={handleFromDateChange}
                format={"DD/MM/YYYY"}
                maxDate={dayjs()}
                minDate={dayjs("2023-07-01")}
                shouldDisableDate={(date) =>
                  date.isAfter(toDate, "day") ||
                  date.isBefore(dayjs("2023-07-01"), "day")
                }
                sx={datePickerSx}
                slots={datePickerSlots}
                slotProps={datePickerSlotProps}
              />
              <DatePicker
                label="To"
                value={toDate}
                onChange={handleToDateChange}
                format={"DD/MM/YYYY"}
                maxDate={dayjs()}
                minDate={fromDate}
                shouldDisableDate={(date) => date.isAfter(dayjs(), "day")}
                sx={datePickerSx}
                slots={datePickerSlots}
                slotProps={datePickerSlotProps}
              />
            </Box>
          </LocalizationProvider>
        </Box>

        {isLoading ? (
          <Box
            component="main"
            sx={{
              flex: 1,
              py: 6,
              px: 4,
              bgcolor: colors.mainContentBgColor,
              height: "80vh",
            }}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress color="success" />
          </Box>
        ) : (
          <Box
            my="1.5rem"
            sx={{
              mx: { xs: "1rem", md: "2.5rem" },
            }}
          >
            <Box
              sx={{
                mt: 2,
                height: data.length === 0 ? "25vh" : "auto",
              }}
            >
              <TableDatagrid
                data={data}
                columns={isSmallScreen ? mobilecolumns : desktopColumns}
                isMobile={isSmallScreen}
                fontSize="11.5px"
              />
            </Box>
          </Box>
        )}
      </Box>

      <SnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={error}
        statusCode={statusCode}
      />
    </>
  );
};

export default ChargingSessions;
